import { useEffect, useState } from "react";

import { useTranslate } from "@/common/hooks/useTranslate";

interface AuctionCountdown {
  label: string | null;
}

const MILLISECONDS_PER_SECOND = 1000;
const MILLISECONDS_PER_MINUTE = 60 * MILLISECONDS_PER_SECOND;
const MILLISECONDS_PER_HOUR = 60 * MILLISECONDS_PER_MINUTE;
const MILLISECONDS_PER_DAY = 24 * MILLISECONDS_PER_HOUR;

function computeCountdownLabel(
  endDateUtc: string,
  t: ReturnType<typeof useTranslate>["t"],
  extended: boolean
): string | null {
  const remainingMs = new Date(endDateUtc).getTime() - Date.now();

  if (remainingMs <= 0) {
    return null;
  }

  const days = Math.floor(remainingMs / MILLISECONDS_PER_DAY);
  if (days >= 1) {
    if (extended) {
      const hours = Math.floor(remainingMs / MILLISECONDS_PER_HOUR);
      const minutes = Math.floor((remainingMs % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_MINUTE);
      return t("ticketing", "auctionV2.countdown.extended.hoursMinutes", { hours, minutes });
    }
    return t("ticketing", "auctionV2.countdown.days", { count: days });
  }

  const hours = Math.floor(remainingMs / MILLISECONDS_PER_HOUR);
  const minutes = Math.floor((remainingMs % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_MINUTE);
  if (hours >= 1) {
    return extended
      ? t("ticketing", "auctionV2.countdown.extended.hoursMinutes", { hours, minutes })
      : t("ticketing", "auctionV2.countdown.hoursMinutes", { hours, minutes });
  }

  const seconds = Math.floor((remainingMs % MILLISECONDS_PER_MINUTE) / MILLISECONDS_PER_SECOND);
  return extended
    ? t("ticketing", "auctionV2.countdown.extended.minutesSeconds", { minutes, seconds })
    : t("ticketing", "auctionV2.countdown.minutesSeconds", { minutes, seconds });
}

interface UseAuctionCountdownOptions {
  extended?: boolean;
}

export function useAuctionCountdown(
  endDateUtc: string | undefined,
  options?: UseAuctionCountdownOptions
): AuctionCountdown {
  const { t } = useTranslate();
  const extended = options?.extended ?? false;

  const [label, setLabel] = useState<string | null>(() =>
    endDateUtc ? computeCountdownLabel(endDateUtc, t, extended) : null
  );

  useEffect(() => {
    if (!endDateUtc) {
      setLabel(null);
      return;
    }

    setLabel(computeCountdownLabel(endDateUtc, t, extended));

    const remainingMs = new Date(endDateUtc).getTime() - Date.now();
    const isUnderOneHour = remainingMs > 0 && remainingMs < MILLISECONDS_PER_HOUR;
    const isUnderOneDay = remainingMs > 0 && remainingMs < MILLISECONDS_PER_DAY;
    const intervalMs = isUnderOneHour
      ? MILLISECONDS_PER_SECOND
      : isUnderOneDay
        ? MILLISECONDS_PER_MINUTE
        : MILLISECONDS_PER_HOUR;

    const intervalId = setInterval(() => {
      const newLabel = computeCountdownLabel(endDateUtc, t, extended);
      setLabel(newLabel);

      if (newLabel === null) {
        clearInterval(intervalId);
      }
    }, intervalMs);

    return () => clearInterval(intervalId);
  }, [endDateUtc, t, extended]);

  return { label };
}
